import { motion } from "framer-motion";
import { PERSPECTIVES } from "@/data";

function Globe({ className }) {
    return (
        <svg
            viewBox="0 0 48 48"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.1"
            className={className}
            aria-label="Worldwide"
            data-testid="about-globe-icon"
        >
            <circle cx="24" cy="24" r="21" />
            <ellipse cx="24" cy="24" rx="9" ry="21" />
            <path d="M3 24h42M6.5 13.5h35M6.5 34.5h35" />
        </svg>
    );
}

const STATS = [
    { value: "360°", label: "Immersive walkthroughs" },
    { value: "Drone", label: "Aerial cinematography" },
    {
        value: (
            <span className="inline-flex items-center gap-3" data-testid="about-la-worldwide">
                LA
                <span className="font-light">→</span>
                <Globe className="h-[0.78em] w-[0.78em] text-[#121211]" />
            </span>
        ),
        label: "Based in Los Angeles",
    },
];

const reveal = {
    initial: { opacity: 0, y: 36 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

export default function About() {
    return (
        <section
            id="about"
            className="bg-[#FBF9F5] px-6 py-24 lg:px-10 lg:py-36"
            data-testid="about-section"
        >
            <div className="mx-auto max-w-[1400px]">
                <motion.p
                    {...reveal}
                    className="font-mono2 text-[10px] uppercase tracking-[0.34em] text-[#6B6862]"
                >
                    The studio
                </motion.p>
                <motion.h2
                    {...reveal}
                    transition={{ ...reveal.transition, delay: 0.08 }}
                    className="mt-6 font-display text-5xl font-light leading-[1.02] tracking-tight text-[#121211] sm:text-6xl lg:text-7xl"
                    data-testid="about-headline"
                >
                    One crew.
                    <br />
                    <span className="italic">Every angle.</span>
                </motion.h2>

                <div className="mt-12 grid gap-12 lg:mt-16 lg:grid-cols-12 lg:gap-16">
                    <motion.p
                        {...reveal}
                        transition={{ ...reveal.transition, delay: 0.16 }}
                        className="max-w-xl text-base font-light leading-relaxed text-[#121211]/70 lg:col-span-6"
                    >
                        One team. One visual language. Two complementary
                        capabilities. LILIKOOP is a Los Angeles spatial
                        production studio: immersive 360° walkthroughs are the
                        core of what we do; cinematic aerial and drone production
                        completes the picture — for residences, hospitality,
                        wellness, restaurants, commercial spaces and
                        architecture.
                    </motion.p>
                    <motion.ul
                        {...reveal}
                        transition={{ ...reveal.transition, delay: 0.22 }}
                        className="divide-y divide-[#121211]/10 border-y border-[#121211]/10 lg:col-span-5 lg:col-start-8"
                        data-testid="about-perspectives"
                    >
                        {PERSPECTIVES.map((p, i) => (
                            <li
                                key={p.word}
                                className="flex items-baseline justify-between gap-6 py-4"
                            >
                                <span className="font-display text-2xl font-light italic text-[#121211] sm:text-3xl">
                                    {p.word}
                                </span>
                                <span className="text-right font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#6B6862]">
                                    {p.label}
                                </span>
                            </li>
                        ))}
                    </motion.ul>
                </div>

                <div className="mt-16 grid grid-cols-1 gap-px bg-[#121211]/10 sm:grid-cols-3 lg:mt-24">
                    {STATS.map((s, i) => (
                        <motion.div
                            key={s.label}
                            {...reveal}
                            transition={{ ...reveal.transition, delay: 0.06 * i }}
                            className="bg-[#FBF9F5] p-6 lg:p-10"
                            data-testid={`about-stat-${i}`}
                        >
                            <p className="font-display text-4xl font-light tracking-tight lg:text-5xl">
                                {s.value}
                            </p>
                            <p className="mt-3 font-mono2 text-[10px] uppercase tracking-[0.26em] text-[#6B6862]">
                                {s.label}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
